import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from '@hooks/useRouter';
import { Button } from 'antd';
import LogRocket from 'logrocket';
import useMounted from '@hooks/useMounted';
import useTheme from '@hooks/useTheme';
import Themes from '@lib/context/themes';
import useRuntimeConfig, { getDynamicConfig } from '@vl-core/hooks/useConfig';
import AccessibilityContainer from '@components/Accessibility/AccessibilityContainer';
import useAuth from '@vl-core/useAuth';
import { useViewportSize } from 'vl-common/src/hooks/useWindowSize';
import cn from 'classnames';
import { useExtremeZoom } from '@hooks/useZoom';
import { CoreFooter } from 'vl-common/src/components/Footer/Footer';
import { useHARFunctions } from 'vl-common/src/hooks/useHARFunctions';
import TestingModal from 'vl-common/src/components/Footer/TestingModal';
import DumpModal from 'vl-common/src/components/Footer/DumpModal';
import { NavigationFooter } from './NavigationFooter';
import usePolicies from '@hooks/usePolicies';

const Footer: React.FC<{ className?: string }> = ({ className }) => {
  const { t, ready } = useTranslation('common');
  const { user, logRocketInitialised } = useAuth();
  const [logRocketSessionURL, setLogRocketSessionURL] = useState('');
  const isMounted = useMounted();
  const { theme, selectTheme, overrideThemeDetection } = useTheme();
  const { LUCY_ENV, DATABASE_VERSION } = useRuntimeConfig();
  const isProd = LUCY_ENV.includes('prod');
  const router = useRouter();
  const { width: actualWidth } = useViewportSize();
  const isExtremeZoom = useExtremeZoom();
  const { query } = useRouter();
  const shouldRecord = Boolean(window.__cyRecorder);

  const { isLoading: policiesLoading, policiesAccepted } = usePolicies();

  const harProps = useHARFunctions(getDynamicConfig);
  const {
    enterTestingMode,
    exitTestingMode,
    inTestingMode,
    setInTestingMode,
    testingModalOpen,
    setTestingModalOpen,
    dumpModalOpen,
    setDumpModalOpen,
    dumpedHAR
  } = harProps;

  /** Is the user a patient? */
  const isPatient = useMemo(() => {
    return user?.user_type_code === 'PATIENT';
  }, [user]);

  const hasAcceptedPolicies = useMemo(() => policiesAccepted, [policiesAccepted, policiesLoading]);

  const isRegistering =
    router.asPath.includes('/register') ||
    router.asPath.includes('/create-account') ||
    router.asPath.includes('/policy');

  useEffect(() => {
    if (isMounted() && logRocketInitialised) {
      LogRocket.getSessionURL((url) => {
        if (isMounted()) {
          setLogRocketSessionURL(url);
        }
      });
    }
  }, [isMounted, logRocketInitialised]);

  const cycleThemes = () => {
    const themesArray = Object.keys(Themes);
    overrideThemeDetection();

    selectTheme(themesArray[(themesArray.indexOf(theme.id) + 1) % themesArray.length]);
  };

  // if doing an autosso, don't bother displaying the footer, you'll get navigated away anyway
  if ('autosso' in query) return null;

  if (isPatient && !isRegistering && actualWidth <= 768 && !isExtremeZoom && hasAcceptedPolicies) {
    return (
      <>
        <NavigationFooter
          cycleThemes={cycleThemes}
          theme={theme}
          logRocketSessionURL={logRocketSessionURL}
          isProd={isProd}
          DATABASE_VERSION={DATABASE_VERSION}
          harProps={harProps}
        />
        <TestingModal
          visible={testingModalOpen}
          onCancel={() => {
            setTestingModalOpen(false);
          }}
          enterTestingMode={enterTestingMode}
          exitTestingMode={exitTestingMode}
          inTestingMode={inTestingMode}
          setInTestingMode={setInTestingMode}
        />
        <DumpModal
          visible={dumpModalOpen}
          har={dumpedHAR || undefined}
          onCancel={() => {
            setDumpModalOpen(false);
          }}
        />
      </>
    );
  }

  const ThemeButton = () => (
    <Button data-testid="dev-change-theme" onClick={cycleThemes} size="small">
      {t('Theme({{theme_id}})', { theme_id: theme.id })}
    </Button>
  );

  if (!ready) return null;

  return (
    <footer {...{ className }}>
      <CoreFooter
        logRocketSessionURL={logRocketSessionURL}
        ThemeButton={ThemeButton}
        footerClass={cn('footer-container', isExtremeZoom && 'extreme-zoom')}
        AccessibilityContainer={AccessibilityContainer}
        harProps={harProps}
        showCypressButton={shouldRecord}
      />
    </footer>
  );
};

export default Footer;
